/* Sleep and Energy — everything is computed backwards from the hour you must wake */

export const mins = (t) => {
  const [h, m] = String(t).split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};
export const clock = (m) => {
  const x = ((Math.round(m) % 1440) + 1440) % 1440;
  return `${String(Math.floor(x / 60)).padStart(2, "0")}:${String(x % 60).padStart(2, "0")}`;
};

export const WAKE_CHIPS = ["05:30", "06:00", "06:30", "07:00", "07:30", "08:00"];

export const TALK = [
  {
    id: "wake",
    kind: "time",
    prompt: "What hour must you be awake?",
    sub: "One number. Every other hour tonight is measured back from it.",
  },
  {
    id: "pattern",
    kind: "options",
    prompt: "Which part of the night goes wrong?",
    sub: "The one that happens most weeks, not the worst night you ever had.",
    options: [
      { key: "onset", label: "I lie there for an hour before anything happens" },
      { key: "middle", label: "I fall asleep fine and I am awake at three" },
      { key: "early", label: "I wake far too early and cannot get back" },
      { key: "unrested", label: "I sleep the hours and still wake flat" },
      { key: "shift", label: "My shifts move, so no two nights are the same" },
    ],
  },
  {
    id: "need",
    kind: "options",
    prompt: "How much sleep leaves you actually functioning?",
    sub: "Not the ideal number. The honest one.",
    options: [
      { hours: 7, label: "Around seven" },
      { hours: 7.5, label: "Seven and a half" },
      { hours: 8, label: "Eight" },
      { hours: 8.5, label: "More than eight, if I am honest" },
    ],
  },
];

export const PATTERNS = {
  onset: {
    key: "onset",
    name: "A long runway, badly used",
    say: "Your night does not begin at the pillow. It begins ninety minutes earlier, and right now those ninety minutes belong to a screen.",
    read: "Sleep onset is mostly a light and arousal problem, not a willpower problem. Bright overhead light late in the evening holds melatonin back by up to an hour and a half, and a body that has been sitting still and stimulated until the last second has nothing to descend from. The fix is unglamorous: drop the light level early, put the day down on paper before you lie down, and let the body cool.",
    focus: ["lights", "write", "breath"],
    caffeine: 10,
    extra: { k: "nolying", at: -5, kind: "rule", title: "If twenty minutes pass, get up", text: "Leave the bed, stay in low light, do something dull, come back when you feel heavy. The bed should never become the place you practise being awake." },
  },
  middle: {
    key: "middle",
    name: "The three o'clock wake",
    say: "You can fall asleep. It is the second half of the night that keeps handing you back.",
    read: "Waking in the middle of the night is normal, several times a night, for everyone. What decides whether you notice it is what is in your blood and your head. Alcohol fragments the back half of the night almost perfectly. A warm room does the same. And the thought that arrives at 03:00 has never once been solved at 03:00. So we cool the room, move the last drink and the last real meal earlier, and give the thought a place to go before you lie down.",
    focus: ["meal", "cool", "write"],
    caffeine: 10,
    extra: { k: "cool", at: -60, kind: "step", title: "Drop the room by two degrees", text: "Window open, heating off, thinner cover. The body must lose about half a degree of core heat to hold sleep, and it cannot do that in a warm room." },
  },
  early: {
    key: "early",
    name: "The clock that runs ahead",
    say: "Your body clock has drifted early, so it hands you the morning while it is still dark.",
    read: "Waking at four, wide awake and unable to return, usually means the internal clock is set earlier than your life is. Light is the only lever strong enough to move it. Evening light pushes the clock later, morning light pulls it earlier, and right now you are probably getting the wrong one at the wrong time. So the evening gets brighter than you think, the morning stays dim for the first hour, and the bedtime moves later rather than earlier.",
    focus: ["eveninglight", "lights", "bed"],
    caffeine: 8,
    extra: { k: "eveninglight", at: -120, kind: "step", title: "Keep the evening brighter than feels right", text: "Two hours before bed, do not sit in the dark. Normal light here pushes the clock later, which is the direction you need it to move." },
  },
  unrested: {
    key: "unrested",
    name: "Enough hours, wrong shape",
    say: "The hours are there. What is missing is depth, and depth is decided long before midnight.",
    read: "Sleeping eight hours and waking flat usually points at fragmentation rather than duration: caffeine still circulating at midnight, a late heavy meal, alcohol, a room that is too warm, or a wake time that moves by two hours across the week. Deep sleep is front loaded, so anything that delays the first hour costs you the most valuable part of the night. The wake time gets nailed down first, everything else follows it.",
    focus: ["caffeine", "anchor", "walk"],
    caffeine: 12,
    extra: { k: "anchor", at: 1, kind: "rule", title: "The wake time does not move, not even Sunday", text: "One anchor, seven days, within forty minutes. A body clock cannot be built on a schedule that moves two hours every weekend." },
  },
  shift: {
    key: "shift",
    name: "A night that has to be portable",
    say: "You cannot keep the same hours, so we build something that survives being moved.",
    read: "Rotating shifts break the one thing sleep depends on: a stable clock. The answer is not a perfect schedule, it is a portable ritual and hard control of light. The same sequence in the same order tells the body what is coming, whether that is at 22:00 or at 09:00. Dark on the way home, one strong bright block when your day starts, caffeine only in the first half of a shift, and a proper anchor sleep of at least four hours at the same time every day.",
    focus: ["ritual", "lights", "screens"],
    caffeine: 8,
    extra: { k: "ritual", at: -30, kind: "rule", title: "The same four moves, in the same order", text: "Shower, low light, two minutes on paper, breath. It does not matter what hour it happens at. The order is the signal." },
  },
};

/* the shared spine — offsets in minutes relative to lights out */
export const STEPS = [
  /* `at` for caffeine is replaced by the pattern's own cut off in buildRunway */
  { k: "caffeine", at: -600, kind: "marker", title: "Last caffeine of the day", text: "Half of it is still in you six hours later. This is the hour it stops, not the hour you would like it to stop." },
  { k: "meal", at: -180, kind: "marker", title: "Last real meal", text: "Digestion raises core temperature, and sleep needs it to fall. Something small after this is fine, a plate is not." },
  { k: "walk", at: -150, kind: "step", minutes: 10, title: "Ten minutes outside", text: "Any light still in the sky counts, and the movement burns off the last of the day's tension. This is the cheapest thing on the whole runway." },
  { k: "lights", at: -90, kind: "step", title: "Overhead lights off, lamps only", text: "The runway starts here. Everything above head height goes off, everything low and warm stays on. This one change is worth more than any supplement." },
  { k: "warm", at: -75, kind: "step", minutes: 8, title: "Warm shower, then let yourself cool", text: "The warmth pulls blood to the skin, and the drop afterwards is the exact signal the brain reads as night." },
  { k: "screens", at: -60, kind: "step", title: "Screens go down, and stay down", text: "Not for the blue light. For the fact that a feed is designed to keep you here, and you have somewhere else to be." },
  { k: "write", at: -45, kind: "step", minutes: 3, title: "Three minutes on paper", text: "Tomorrow's three things, and anything circling. A thought that has been written down stops asking to be remembered." },
  { k: "breath", at: -20, kind: "step", minutes: 6, title: "Slow the breath down", text: "In for four, out for six, six minutes. Long exhales are the one voluntary switch into the calm branch of the nervous system." },
  { k: "bed", at: 0, kind: "anchor", title: "In bed, lights out", text: "Cool, dark, quiet, and no clock facing you." },
];

export const MORNING = { k: "morning", title: "Light in your eyes within thirty minutes", text: "Outside if possible, window if not, ten minutes either way. This is the hour that decides how easily tonight arrives." };

export const buildRunway = (wake, need, pattern) => {
  const p = PATTERNS[pattern] || PATTERNS.onset;
  const bed = mins(wake) - Math.round(need * 60);
  const all = [...STEPS];
  if (p.extra) all.push(p.extra);
  const items = all
    .filter((s) => s.at <= 0)
    .map((s) => (s.k === "caffeine" ? { ...s, at: -p.caffeine * 60 } : s))
    .sort((a, b) => a.at - b.at)
    .map((s) => ({ ...s, time: clock(bed + s.at), focus: (p.focus || []).includes(s.k) }));
  const rules = all.filter((s) => s.at > 0).map((s) => ({ ...s, focus: true }));
  return {
    bed: clock(bed),
    wake: wake,
    caffeineCut: clock(bed - p.caffeine * 60),
    caffeineHours: p.caffeine,
    runwayStart: clock(bed - 90),
    items,
    rules,
    pattern: p,
  };
};

/* ---------------------------------------------------- the body clock dial */
export const CLOCK_WINDOWS = [
  { k: "light", label: "Light window", from: 0, to: 120, tone: "#EBD9A6", say: "The first two hours after you wake. Ten minutes of real light here is worth more than anything you can buy." },
  { k: "peak", label: "Sharpest hours", from: 150, to: 330, tone: "#8FD0E8", say: "Two to five and a half hours after waking. Put the hardest thinking of the day inside this window." },
  { k: "dip", label: "The afternoon dip", from: 420, to: 540, tone: "#6B74DC", say: "Seven to nine hours after waking. Real, biological, and not a character flaw. Twenty minutes of nap or a walk, never a third coffee." },
  { k: "move", label: "Best hours to train", from: 540, to: 690, tone: "#7FC7A2", say: "Nine to eleven and a half hours after waking, when body temperature and strength both peak." },
  { k: "cut", label: "Caffeine is over", from: 480, to: 480, tone: "#E2857A", point: true, say: "Eight to ten hours after waking, depending on how it sits in you. After this it is buying tonight's sleep to pay for this afternoon." },
  { k: "melatonin", label: "Melatonin begins", from: 840, to: 900, tone: "#B7A6F0", say: "About fourteen hours after waking, and only if the light around you is low. Bright overhead light holds it back by up to ninety minutes." },
  { k: "gate", label: "The easiest hour to fall asleep", from: 930, to: 1020, tone: "#5A63C8", say: "Fifteen and a half to seventeen hours after waking. Miss it and the body opens a second wind that can hold you up for another hour." },
];

/* ------------------------------------------------------------- the mixer */
export const LAYERS = [
  { k: "rain", label: "Rain", hint: "steady, on a flat roof", tone: "#8FD0E8" },
  { k: "wind", label: "Wind", hint: "far off, moving through trees", tone: "#A7B0E8" },
  { k: "embers", label: "Embers", hint: "low, close, irregular", tone: "#E2A07A" },
  { k: "hum", label: "Low hum", hint: "the room itself, barely there", tone: "#B7A6F0" },
];

export const MIXES = [
  { name: "Rain on a tin roof", levels: { rain: 0.75, wind: 0.15, embers: 0, hum: 0.12 } },
  { name: "Somewhere in a forest", levels: { rain: 0.2, wind: 0.6, embers: 0.28, hum: 0.1 } },
  { name: "The room hums", levels: { rain: 0, wind: 0.1, embers: 0.05, hum: 0.55 } },
  { name: "Almost nothing", levels: { rain: 0.12, wind: 0.08, embers: 0, hum: 0.06 } },
];

/* ------------------------------------------------------- fourteen nights */
export const NIGHT_Q = [
  { k: "onset", label: "How long until you were gone?", options: ["Under 15 min", "15 to 40", "40 to 90", "Most of the night"] },
  { k: "wakes", label: "How many times did you surface?", options: ["None I remember", "Once", "Two or three", "More than three"] },
  { k: "energy", label: "And this morning?", options: ["Actually rested", "Usable", "Flat", "Wrecked"] },
];

export const nightScore = (n) =>
  n ? Math.round(((3 - n.onset) + (3 - n.wakes) + (3 - n.energy)) / 9 * 100) : null;

export const SHAPE_READ = (avg, count) => {
  if (count < 3) return "Three nights is the minimum before a shape means anything. Keep going.";
  if (avg >= 72) return "This is a good shape. The thing to protect now is the wake time, because that is what holds it.";
  if (avg >= 50) return "A workable shape with holes in it. Look at which of the three answers is dragging the line down, and put the runway step for it first tonight.";
  return "A hard fortnight. This is the point where the runway matters more than any single night does, and the point where it is worth telling a doctor.";
};

export const NAPS = [
  { min: 20, name: "The twenty", say: "Light sleep only. You wake clear, it does not touch tonight, and it is the correct answer to the afternoon dip.", tone: "#8FD0E8" },
  { min: 45, name: "The forty five", say: "Long enough to fall into deep sleep and be pulled out of it. You will wake worse than you lay down. Avoid this one.", tone: "#E2857A", warn: true },
  { min: 90, name: "The ninety", say: "A full cycle, out the other side. Restorative, and only if the night ahead can afford it.", tone: "#7FC7A2" },
];

export const ASKED = [
  { q: "Is eight hours actually the number?", a: "For most adults the range is seven to nine, and where you sit in it is largely genetic. The useful number is not hours in bed, it is whether you can get through the afternoon without caffeine and without wanting to lie down. If you can, you are sleeping enough, whatever the number says." },
  { q: "Do I need to track it with a watch?", a: "No. Consumer trackers are decent at duration and poor at stages, and for a lot of people the number becomes the anxiety. Two questions in the morning, how long it took and how you feel, tell you more than a graph does." },
  { q: "What about melatonin tablets?", a: "Useful for moving a clock, jet lag or shift rotation, in small doses of a half to one milligram taken hours before bed. Close to useless as a sedative on a normal night, and the doses sold in shops are usually five to ten times higher than the ones studied." },
  { q: "Is one glass of wine really a problem?", a: "It gets you to sleep faster and then removes the back half of the night. Alcohol suppresses the dreaming sleep that consolidates memory and mood, and fragments the last three hours. If your complaint is waking at three, this is the first thing to move." },
  { q: "When is this a doctor's problem?", a: "Loud snoring with pauses, gasping, or morning headaches point at sleep apnoea and need a proper test. Three months of insomnia, sleep that is fine but daytime that is not, legs that will not settle, or acting out dreams all deserve a referral rather than another app." },
];
